import { Injectable } from "../../../lib/web/reflection/injectable";

export interface SearchFilterNavigationHandlers {
	close(): void;
	search(): void;
	toggleGroup(groupElement: HTMLDivElement): void;
}

@Injectable({ type: 'transient' })
export class SearchFilterNavigationComponent {

	private _node: HTMLElement = null;
	private _groups: HTMLDivElement[] = [];
	private _handlers: SearchFilterNavigationHandlers = null;
	private _groupContentObserver: MutationObserver = null;
	private _documentArrowKeyBlocker: (event: KeyboardEvent) => void = null;

	public attach(node: HTMLElement, groups: HTMLDivElement[], handlers: SearchFilterNavigationHandlers): void {
		this._node = node;
		this._groups = groups || [];
		this._handlers = handlers;
	}

	public onKeyDown(event: KeyboardEvent): void {
		if (!this._node?.classList.contains('m-search-filter--opened')) {
			return;
		}

		switch (event.key) {
			case 'Escape':
				event.preventDefault();
				this._handlers?.close();
				break;
			case 'Tab':
				break;
			case 'Enter':
			case ' ': // Space
				event.preventDefault();
				this.activateCurrentElement();
				break;
		}
	}

	public enable(): void {
		if (!this._node) {
			return;
		}

		this._node.setAttribute('data-keyboard-navigation', 'true');
		this.ensureDocumentArrowKeyBlocker();
		this.ensureGroupContentObserver();
		this.refreshTabIndexes();

		setTimeout(() => {
			const closeButton: HTMLElement = this._node.querySelector('.m-search-filter__close');
			if (closeButton) {
				closeButton.focus();
			}
		}, 100);
	}

	public disable(): void {
		if (!this._node) {
			return;
		}

		this._node.removeAttribute('data-keyboard-navigation');

		if (this._documentArrowKeyBlocker) {
			document.removeEventListener('keydown', this._documentArrowKeyBlocker, true);
			this._documentArrowKeyBlocker = null;
		}

		if (this._groupContentObserver) {
			this._groupContentObserver.disconnect();
			this._groupContentObserver = null;
		}

		this._node.querySelectorAll('.keyboard-focus').forEach(el => el.classList.remove('keyboard-focus'));
	}

	public refreshTabIndexes(): void {
		this._groups.forEach(group => {
			const contentElement: HTMLElement = group.querySelector('.m-search-filter__group-content') as HTMLElement;
			if (!contentElement) {
				return;
			}

			const isExpanded: boolean = contentElement.classList.contains('m-search-filter__group-content--opened');
			const optionTabIndex: string = isExpanded ? '0' : '-1';

			const isToggleGroup: boolean = contentElement.classList.contains('m-search-filter__group-content--toggle');

			if (isToggleGroup) {
				const toggleButtons: NodeListOf<HTMLButtonElement> = contentElement.querySelectorAll('.a-toggle-selector__item');
				toggleButtons.forEach(btn => {
					btn.setAttribute('tabindex', optionTabIndex);
					btn.tabIndex = isExpanded ? 0 : -1;
				});
				return;
			}

			const optionElements: NodeListOf<HTMLElement> = contentElement.querySelectorAll('[role="option"]');
			optionElements.forEach(option => {
				option.setAttribute('tabindex', optionTabIndex);
				option.tabIndex = isExpanded ? 0 : -1;
			});

			const innerControls: NodeListOf<HTMLElement> = contentElement.querySelectorAll('input, button, a, select, textarea');
			innerControls.forEach(control => {
				control.setAttribute('tabindex', '-1');
				control.tabIndex = -1;
			});
		});
	}


	private activateCurrentElement(): void {
		const focusedElement: HTMLElement = document.activeElement as HTMLElement;

		if (focusedElement?.classList.contains('m-search-filter__group-header')) {
			const groupElement = focusedElement.closest('.m-search-filter__group') as HTMLDivElement;
			this._handlers?.toggleGroup(groupElement);
		} 
		else if (focusedElement?.classList.contains('m-search-filter__close')) {
			this._handlers?.close();
		} 
		else if (focusedElement?.classList.contains('m-search-filter__search')) {
			this._handlers?.search();
		} 
		else {
			if (focusedElement?.getAttribute('role') === 'option') {
				focusedElement.click();
				return;
			}

			const optionElement: HTMLElement = focusedElement?.closest('[role="option"]') as HTMLElement;
			if (optionElement) {
				optionElement.click();
			}
		}
	}

	private focusElement(element: HTMLElement): void {
		element.focus();
		this._node.querySelectorAll('.keyboard-focus').forEach(el => el.classList.remove('keyboard-focus'));
		element.classList.add('keyboard-focus');
	}

	private ensureGroupContentObserver(): void {
		if (this._groupContentObserver) {
			return;
		}
		if (typeof MutationObserver === 'undefined') {
			return;
		}

		this._groupContentObserver = new MutationObserver(mutations => {
			if (!this._node.classList.contains('m-search-filter--opened')) {
				return;
			}
			const hasRelevantChange: boolean = mutations.some(m => m.type === 'attributes' && m.attributeName === 'class');
			if (!hasRelevantChange) {
				return;
			}
			requestAnimationFrame(() => {
				this.refreshTabIndexes();
			});
		});

		this._groupContentObserver.observe(this._node, {
			subtree: true,
			attributes: true,
			attributeFilter: ['class'],
		});
	}

	private ensureDocumentArrowKeyBlocker(): void {
		if (this._documentArrowKeyBlocker) {
			return;
		}

		this._documentArrowKeyBlocker = (event: KeyboardEvent) => {
			if (!this._node?.classList.contains('m-search-filter--opened')) {
				return;
			}
			const target: Node = event.target as Node;
			const activeElement: Node = document.activeElement as Node;
			const isFromFilter: boolean = (target && this._node.contains(target)) || (activeElement && this._node.contains(activeElement));
			if (!isFromFilter) {
				return;
			}
			if (this.isLeftArrowKey(event)) {
				this.collapseCurrentGroup(event);
				return;
			}
			if (!this.isAnyArrowKey(event)) {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			(event as Event).stopImmediatePropagation?.();
		};

		document.addEventListener('keydown', this._documentArrowKeyBlocker, true);
	}

	private isAnyArrowKey(event: KeyboardEvent): boolean {
		const key: string = event.key;
		return (typeof key === 'string' && key.startsWith('Arrow')) || (event.keyCode >= 37 && event.keyCode <= 40);
	}

	private isLeftArrowKey(event: KeyboardEvent): boolean {
		return event.key === 'ArrowLeft' || event.keyCode === 37;
	}

	private collapseCurrentGroup(event: KeyboardEvent): void {
		event.preventDefault();
		event.stopPropagation();
		(event as Event).stopImmediatePropagation?.();

		const target: HTMLElement = (event.target as HTMLElement) || (document.activeElement as HTMLElement);
		if (!target) {
			return;
		}

		const groupElement: HTMLDivElement = target.closest('.m-search-filter__group') as HTMLDivElement;
		if (!groupElement) {
			return;
		}

		const contentElement: HTMLDivElement = groupElement.querySelector('.m-search-filter__group-content') as HTMLDivElement;
		if (!contentElement?.classList.contains('m-search-filter__group-content--opened')) {
			return;
		}

		this._handlers?.toggleGroup(groupElement);

		const headerElement: HTMLElement = groupElement.querySelector('.m-search-filter__group-header') as HTMLElement;
		if (headerElement) {
			this.focusElement(headerElement);
		}
	}
}
